import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@asad/crane-ui'

import {
  DEFAULT_VIEW_ID,
  automaticPositions,
  createDraft,
  draftsEqual,
  nextViewName,
  normalizePositions,
  type DashboardView,
  type DashboardViewDraft,
} from '../model/dashboardViews'
import { useDashboardViews } from './useDashboardViews'

type EditorState = {
  mode: 'create' | 'edit'
  viewId?: string
  initial: DashboardViewDraft
  draft: DashboardViewDraft
}

type UseDashboardViewManagementOptions = {
  profileId?: string
  availableDeviceIds: string[]
  onViewChanged: () => void
}

const createViewId = (existingIds: Set<string>): string => {
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const randomPart =
      globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    const id = `view-${randomPart}`
    if (!existingIds.has(id)) return id
  }

  return `view-${Date.now().toString(36)}-${existingIds.size}`
}

/** Orkiestruje serwerowe Widoki i lokalny szkic edytora; dane trwałe pozostają w Reduxie. */
export const useDashboardViewManagement = ({
  profileId,
  availableDeviceIds,
  onViewChanged,
}: UseDashboardViewManagementOptions) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const {
    state: viewsState,
    loadStatus: viewsLoadStatus,
    saveStatus: viewsSaveStatus,
    commit: commitViewsState,
    reload: reloadViews,
  } = useDashboardViews(profileId)
  const [editor, setEditor] = React.useState<EditorState | null>(null)
  const [deleteTarget, setDeleteTarget] = React.useState<DashboardView | null>(null)
  const [discardAction, setDiscardAction] = React.useState<null | (() => void)>(null)

  const viewsSaving = viewsSaveStatus === 'loading'
  const editorDirty = Boolean(editor && !draftsEqual(editor.initial, editor.draft))
  const activeView = React.useMemo(
    () => viewsState.views.find(({ id }) => id === viewsState.activeViewId),
    [viewsState],
  )

  const requestAction = React.useCallback(
    (action: () => void) => {
      if (viewsSaving) return
      if (editorDirty) setDiscardAction(() => action)
      else action()
    },
    [editorDirty, viewsSaving],
  )
  const exitEditor = React.useCallback(
    (action?: () => void) =>
      requestAction(() => {
        setEditor(null)
        action?.()
      }),
    [requestAction],
  )

  const activateView = React.useCallback(
    async (id: string) => {
      if (viewsSaving || id === viewsState.activeViewId) return

      try {
        await commitViewsState({ ...viewsState, activeViewId: id })
        onViewChanged()
        toast.success(t('dashboardViews.activated'))
      } catch {
        // Odrzucony thunk pokazuje errorLoggingMiddleware i pozostawia poprzedni aktywny widok.
      }
    },
    [commitViewsState, onViewChanged, t, toast, viewsSaving, viewsState],
  )

  const startCreate = React.useCallback(() => {
    const initial = createDraft({
      id: '',
      name: nextViewName(
        viewsState.views.map(({ name }) => name),
        (number) => t('dashboardViews.defaultName', { number }),
      ),
      deviceIds: availableDeviceIds,
      positions: automaticPositions(availableDeviceIds),
      updatedAt: '',
    })
    setEditor({ mode: 'create', initial, draft: createDraft({ ...initial, id: '', updatedAt: '' }) })
  }, [availableDeviceIds, t, viewsState.views])

  const startEdit = React.useCallback((view: DashboardView) => {
    const initial = createDraft(view)
    setEditor({ mode: 'edit', viewId: view.id, initial, draft: createDraft(view) })
  }, [])

  const saveEditor = React.useCallback(async () => {
    if (!editor || viewsSaving) return

    const savedId = editor.mode === 'edit' ? editor.viewId : createViewId(new Set(viewsState.views.map(({ id }) => id)))
    if (!savedId) return

    const saved: DashboardView = {
      id: savedId,
      name: editor.draft.name.trim(),
      deviceIds: [...editor.draft.deviceIds],
      positions: normalizePositions(editor.draft.deviceIds, editor.draft.positions),
      updatedAt: new Date().toISOString(),
    }

    try {
      await commitViewsState({
        views:
          editor.mode === 'edit'
            ? viewsState.views.map((view) => (view.id === saved.id ? saved : view))
            : [...viewsState.views, saved],
        activeViewId: saved.id,
      })
      setEditor(null)
      onViewChanged()
      toast.success(t('dashboardViews.saved'))
    } catch {
      // Odrzucony thunk pokazuje errorLoggingMiddleware; edytor zostaje otwarty z bieżącym szkicem.
    }
  }, [commitViewsState, editor, onViewChanged, t, toast, viewsSaving, viewsState.views])

  const removeView = React.useCallback(async () => {
    if (!deleteTarget || viewsSaving) return

    try {
      await commitViewsState({
        views: viewsState.views.filter(({ id }) => id !== deleteTarget.id),
        activeViewId: viewsState.activeViewId === deleteTarget.id ? DEFAULT_VIEW_ID : viewsState.activeViewId,
      })
      setDeleteTarget(null)
      toast.success(t('dashboardViews.deleted'))
    } catch {
      // Odrzucony thunk pokazuje errorLoggingMiddleware; potwierdzenie zostaje otwarte do ponowienia.
    }
  }, [commitViewsState, deleteTarget, t, toast, viewsSaving, viewsState])

  React.useEffect(() => {
    if (!editorDirty) return

    const preventUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    const preventNavigation = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return

      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest('a[href]')
      if (!(anchor instanceof HTMLAnchorElement) || anchor.target === '_blank' || anchor.hasAttribute('download'))
        return
      const url = new URL(anchor.href, window.location.href)
      if (url.origin !== window.location.origin) return
      if (url.pathname === window.location.pathname && url.search === window.location.search) return

      event.preventDefault()
      setDiscardAction(() => () => {
        setEditor(null)
        navigate(`${url.pathname}${url.search}${url.hash}`)
      })
    }

    window.addEventListener('beforeunload', preventUnload)
    document.addEventListener('click', preventNavigation, true)
    return () => {
      window.removeEventListener('beforeunload', preventUnload)
      document.removeEventListener('click', preventNavigation, true)
    }
  }, [editorDirty, navigate])

  return {
    viewsState,
    viewsLoadStatus,
    viewsSaving,
    reloadViews,
    activeView,
    editor,
    editorDirty,
    deleteTarget,
    discardAction,
    activateView,
    startCreate,
    startEdit,
    saveEditor,
    removeView,
    exitEditor,
    setEditorDraft: (draft: DashboardViewDraft) => setEditor((current) => (current ? { ...current, draft } : current)),
    closeEditor: () => exitEditor(),
    openDelete: setDeleteTarget,
    cancelDelete: () => {
      if (!viewsSaving) setDeleteTarget(null)
    },
    confirmDiscard: () => {
      const action = discardAction
      setDiscardAction(null)
      action?.()
    },
    cancelDiscard: () => setDiscardAction(null),
  }
}
